import { createSelector, createStructuredSelector } from "reselect"

import { selectOrganizationName } from "shared/userdata/userdata-selectors"
import { selectLedgerSettingsList } from "shared/pac/selectors"
import { actionCenterPaintings } from "grassroots/components/helpTexts"
import { checkPermission, hasAnyPermission } from "shared/imports/permissionFunctions"
import { getActionCenterPaths } from "app/static/frontend/imports/desktopPaths"
import { QuorumBlue } from "design-constants"

const selectGrassroots = (state, _) => state.grassroots

const selectActionCenters = createSelector(selectGrassroots, (grassroots) => grassroots.get("actionCenters"))

const createActionCentersPropertySelector = (property) =>
    createSelector(selectActionCenters, (actionCenters) => actionCenters && actionCenters.get(property))

const selectActionCentersItems = createActionCentersPropertySelector("items")
const selectActionCentersAreLoading = createActionCentersPropertySelector("loading")
const selectHasLoadedActionCenterList = createActionCentersPropertySelector("loaded")

export const selectActionCentersList = createSelector(selectActionCentersItems, (actionCenters) =>
    actionCenters ? Object.values(actionCenters.toJS()) : [],
)

export const selectHasSingleActionCenter = createSelector(
    selectActionCentersList,
    (actionCenters) => actionCenters.length === 1,
)

export const actionCenterListConnection = createStructuredSelector({
    items: selectActionCentersList,
    orgName: selectOrganizationName,
    isLoading: selectActionCentersAreLoading,
    hasLoaded: selectHasLoadedActionCenterList,
    hasSingleActionCenter: selectHasSingleActionCenter,
})

export const selectActionCenterIdFromRouteOrProp = (_, props) =>
    props && (props.actionCenterId || (props.params && props.params.actionCenterId))

export const selectActionCenterForHome = createSelector(
    selectActionCentersItems,
    selectActionCenterIdFromRouteOrProp,
    (actionCenters, id) => actionCenters && (actionCenters.get(id) || actionCenters.get(String(id))),
)

const createActionCenterPropertySelector = (property) =>
    createSelector(selectActionCenterForHome, (actionCenter) => actionCenter && actionCenter.get(property))

export const selectActionCenterLoaded = createSelector(createActionCenterPropertySelector("id"), (actionCenterId) =>
    Boolean(actionCenterId),
)

const selectActionCenterName = createActionCenterPropertySelector("name")
const selectActionCenterDomain = createActionCenterPropertySelector("domain")
const selectActionCenterIsLoading = createActionCenterPropertySelector("loading")
const selectActionCenterExtra = createActionCenterPropertySelector("_extra")
export const selectActionCenterLedgerSettings = createActionCenterPropertySelector("ledger_settings")
export const selectActionCenterDefaultRegistrationPage = createActionCenterPropertySelector("default_registration_page")
export const selectActionCenterIndexPage = createActionCenterPropertySelector("index_page")
export const selectActionCenterGamificationEnabled = createActionCenterPropertySelector("gamification_enabled")

const selectActionCenterColor = createSelector(
    selectActionCenterExtra,
    (extra) => (extra && extra.get("primary_color")) || QuorumBlue,
)

export const selectLedgerSettingsId = createSelector(selectActionCenterLedgerSettings, (ledgerSettingsUri) =>
    DjangIO.app.ledger.models.LedgerSettings.idFromResourceUri(ledgerSettingsUri),
)

// LedgerSettings are already fetched on page load
// use PAC selector to get the list, find related LedgerSettings in that list
export const selectLedgerSettings = createSelector(
    selectLedgerSettingsList,
    selectLedgerSettingsId,
    (ledgerSettingsList, ledgerSettingsId) =>
        ledgerSettingsList && ledgerSettingsList.find((l) => l.get("id") === Number(ledgerSettingsId)),
)

export const selectHasCompletedStripeOnboarding = createSelector(
    selectLedgerSettings,
    (ledgerSettings) => ledgerSettings && ledgerSettings.get("has_completed_stripe_onboarding", false),
)

export const selectActionCenterHomePaintings = createSelector(selectActionCenterIdFromRouteOrProp, (id) => {
    const { PermissionType } = DjangIO.app.models

    return [
        {
            segueLink: getActionCenterPaths(id)._actionCenterRegistration,
            permissions: [],
            ...actionCenterPaintings.registration,
        },
        checkPermission(PermissionType.can_access_issue_management) && {
            segueLink: getActionCenterPaths(id)._actionCenterIssues,
            permissions: [
                PermissionType.action_center,
                PermissionType.external_interactions,
                PermissionType.external_relationship,
            ],
            ...actionCenterPaintings.issues,
        },
        {
            segueLink: getActionCenterPaths(id)._actionCenterSettings,
            permissions: [PermissionType.can_change_action_center_settings],
            ...actionCenterPaintings.settings,
        },
        !Userdata.i360_client_with_stakeholder && {
            segueLink: getActionCenterPaths(id)._actionCenterCampaigns,
            permissions: [PermissionType.action_center_campaigns],
            ...actionCenterPaintings.campaigns,
        },
        {
            segueLink: getActionCenterPaths(id)._actionCenterPages,
            permissions: [PermissionType.action_center],
            ...actionCenterPaintings.pages,
        },
        {
            segueLink: getActionCenterPaths(id)._actionCenterEvents,
            permissions: [PermissionType.custom_events],
            ...actionCenterPaintings.events,
        },
    ]
        .filter((item) => item && hasAnyPermission(item.permissions))
        .sort((a, b) => a.order - b.order)
})

export const actionCenterHomeConnection = createStructuredSelector({
    alreadyLoaded: selectActionCenterLoaded,
    color: selectActionCenterColor,
    domain: selectActionCenterDomain,
    hasSingleActionCenter: selectHasSingleActionCenter,
    id: selectActionCenterIdFromRouteOrProp,
    isLoading: selectActionCenterIsLoading,
    ledgerSettingsId: selectLedgerSettingsId,
    name: selectActionCenterName,
    paintings: selectActionCenterHomePaintings,
})
